import type {
  TestHeaders,
  TestTags,
} from 'src/utils/productionTesting/productionTesting';

import type { Meta } from '../meta';

export const UPDATE_AUTHENTICATED = 'UPDATE_AUTHENTICATED';
export const UPDATE_SERVER_AUTHENTICATED = 'UPDATE_SERVER_AUTHENTICATED';
export const SET_SEEKER_ID = 'SET_SEEKER_ID';
export const UPDATE_TEST_HEADERS = 'UPDATE_TEST_HEADERS';
export const UPDATE_TEST_TAGS = 'UPDATE_TEST_TAGS';
export const SET_TIMEZONE = 'SET_TIMEZONE';
export const SET_CAREER_FEED_AI_ASSISTANT_CHAT_CONTEXT_COOKIE =
  'SET_CAREER_FEED_AI_ASSISTANT_CHAT_CONTEXT_COOKIE';

export interface UserState {
  authenticated?: boolean;
  /**
   * Temporarily syncing server side authentication state in the user store.
   * This is only used during the transition to SSA, and it will be removed
   * once we fully switch to SSA.
   *
   * DON'T USE THIS VALUE TO DETERMINE IF THE USER IS AUTHENTICATED IN UI COMPONENTS.
   */
  serverAuthenticated?: boolean;
  userClientId: string;
  /**
   * If you're selecting this value in a React component or a custom React hook, it's
   * advised to use `useCandidateDetails` hook instead.
   * We're only keeping this value here for the sake of compatibility with Redux actions.
   */
  seekerId?: number;
  sessionId: string;
  /**
   * Stores the value of the `sol_id` cookie in the incoming request.
   * If no `sol_id` cookie is present or is empty, we generate a new `sol_id`
   * and stores it in the this field.
   */
  solId?: string;
  /**
   * Boolean to reflect if the initial request had a `sol_id` cookie.
   */
  hasInitialSolIdCookie?: boolean;
  testHeaders: TestHeaders;
  testTags?: TestTags;
  timezone: string;
  userAgent: string;
  enableZhForSSR?: boolean;
  /**
   * JSON stringified string of the Career Feed AI Assistant candidate chat context value.
   */
  careerFeedAiAssistantChatContextCookie?: string;
}

type UpdateAuthenticatedAction = {
  type: typeof UPDATE_AUTHENTICATED;
  payload: {
    authenticated: boolean;
  };
  meta?: Meta;
};

type UpdateServerAuthenticatedAction = {
  type: typeof UPDATE_SERVER_AUTHENTICATED;
  payload: {
    serverAuthenticated: boolean;
  };
  meta?: Meta;
};

type UpdateTestHeadersAction = {
  type: typeof UPDATE_TEST_HEADERS;
  payload: {
    testHeaders: TestHeaders;
  };
};

type UpdateTestTagsAction = {
  type: typeof UPDATE_TEST_TAGS;
  payload: {
    testTags?: TestTags;
  };
};

type SetTimezoneAction = {
  type: typeof SET_TIMEZONE;
  payload: {
    timezone: string;
  };
};

type SetSeekerIdAction = {
  type: typeof SET_SEEKER_ID;
  payload: {
    id: number;
  };
};

type SetCareerFeedAiAssistantChatContextCookie = {
  type: typeof SET_CAREER_FEED_AI_ASSISTANT_CHAT_CONTEXT_COOKIE;
  payload: { cookieValue?: string };
};

export type UserAction =
  | UpdateAuthenticatedAction
  | UpdateServerAuthenticatedAction
  | SetSeekerIdAction
  | UpdateTestHeadersAction
  | UpdateTestTagsAction
  | SetCareerFeedAiAssistantChatContextCookie
  | SetTimezoneAction;
